import {
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  input,
  ComponentRef,
} from '@angular/core';
import {
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import {
  HtmlTooltipComponent,
  TooltipPosition,
} from './html-tooltip.component';

@Directive({
  selector: '[htmlTooltip]',
  standalone: true,
})
export class HtmlTooltipDirective implements OnDestroy {
  htmlContent = input<string>('', { alias: 'htmlTooltip' });
  tooltipPosition = input<TooltipPosition>('above');

  private overlayRef: OverlayRef | null = null;
  private positionSub: Subscription | null = null;

  constructor(
    private overlay: Overlay,
    private positionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef
  ) {}

  @HostListener('mouseenter')
  show() {
    // 1. Guard Clause: If the content is empty (or just whitespace), do nothing!
    const content = this.htmlContent();
    if (!content || content.trim() === '') {
      return;
    }

    // 2. Prevent duplicates
    if (this.overlayRef?.hasAttached()) {
      return;
    }

    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.getPositions())
      .withPush(false)
      .withViewportMargin(8);

    this.overlayRef = this.overlay.create({ positionStrategy });
    const tooltipPortal = new ComponentPortal(HtmlTooltipComponent);

    let currentPosition: TooltipPosition = this.tooltipPosition();
    let tooltipRef: ComponentRef<HtmlTooltipComponent> | null = null;

    this.positionSub = positionStrategy.positionChanges.subscribe((change) => {
      currentPosition = this.mapConnectedPosition(change.connectionPair);
      if (tooltipRef) {
        tooltipRef.setInput('position', currentPosition);
      }
    });

    tooltipRef = this.overlayRef.attach(tooltipPortal);

    // Pass the content and position into the component
    tooltipRef.setInput('content', content);
    tooltipRef.setInput('position', currentPosition);
  }

  @HostListener('mouseleave')
  hide() {
    this.closeTooltip();
  }

  ngOnDestroy() {
    this.closeTooltip();
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  private closeTooltip() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    if (this.positionSub) {
      this.positionSub.unsubscribe();
      this.positionSub = null;
    }
  }

  // We added panelClass to cleanly identify which fallback the CDK ultimately chose
  private getPositions(): ConnectedPosition[] {
    const above: ConnectedPosition = {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -8,
      panelClass: 'pos-above',
    };
    const below: ConnectedPosition = {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 8,
      panelClass: 'pos-below',
    };
    const left: ConnectedPosition = {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -8,
      panelClass: 'pos-left',
    };
    const right: ConnectedPosition = {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 8,
      panelClass: 'pos-right',
    };

    switch (this.tooltipPosition()) {
      case 'above':
        return [above, below, left, right];
      case 'below':
        return [below, above, right, left];
      case 'left':
        return [left, right, above, below];
      case 'right':
        return [right, left, above, below];
      default:
        return [above, below, left, right];
    }
  }

  // Safely map the CDK's chosen class back to our TooltipPosition string
  private mapConnectedPosition(pair: ConnectedPosition): TooltipPosition {
    if (pair.panelClass === 'pos-above') {
      return 'above';
    }
    if (pair.panelClass === 'pos-below') {
      return 'below';
    }
    if (pair.panelClass === 'pos-left') {
      return 'left';
    }
    if (pair.panelClass === 'pos-right') {
      return 'right';
    }
    return 'above'; // Default fallback
  }
}
